import useSWR from "swr";
import { Chip } from "@/components/badge/chip";
import { Section } from "@/components/section";
import { useAuthStore } from "@/lib/swr";
import { useIsAdmin } from "@/hooks/auth";
import {
  RC_TASK_TYPES,
  RcTask,
  isRcTaskAssignee,
  refreshRcTasks,
} from "@/constants/rc";
import RcRejectButton from "@/components/rcs/rc-reject-button";
import TaskConfirmButton from "@/components/task-confirm/confirm-button";
import TaskDueInput from "@/components/task-confirm/due-input";
import { TaskRowStatus } from "@/components/task-confirm/row-status";
import { taskDue } from "@/components/task-confirm/due";

// RC edit dialog: flow-node tasks (implement / close) with per-assignee
// confirmation. Any listed assignee (or an admin) can confirm; others see the
// due state so the requester can chase. Hidden while no tasks exist.
// Implement confirms are rejected (400) until the RC status / Firebase link
// is written back — the server's guidance is surfaced as a toast.
const RcTasksSection = ({ rcId }: { rcId: string }) => {
  const user = useAuthStore((s) => s.auth?.user);
  const isAdmin = useIsAdmin();
  const { data: tasks } = useSWR<RcTask[]>([`/remote-configs/${rcId}/tasks`]);

  if (!tasks?.length) return null;

  return (
    <Section title="Flow tasks">
      <div className="flex flex-col gap-1.5">
        {tasks.map((task) => {
          const due = taskDue(task);
          const mine = isRcTaskAssignee(task, user?.id);
          return (
            <div key={task.id} className="flex items-center gap-1.5 text-sm">
              <Chip tone={RC_TASK_TYPES[task.type].tone}>
                {RC_TASK_TYPES[task.type].label}
              </Chip>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {(task.assignees ?? []).map((a) => a.name).join(", ") ||
                  "unassigned"}
              </span>
              {task.rejectedAt ? (
                // Rejected approval: terminal — surface the reason inline.
                <Chip tone="red" title={task.rejectReason ?? undefined}>
                  rejected
                </Chip>
              ) : (
                <TaskRowStatus
                  confirmedAt={task.confirmedAt}
                  canAct={mine || isAdmin}
                  due={due}
                >
                  <TaskDueInput
                    endpoint={`/rc-tasks/${task.id}/due`}
                    onSaved={() => refreshRcTasks(task.rcId)}
                    dueAt={task.dueAt}
                    overdue={due.overdue}
                  />
                  <TaskConfirmButton
                    endpoint={`/rc-tasks/${task.id}/confirm`}
                    onConfirmed={() => refreshRcTasks(task.rcId)}
                    errorTitle="Cannot confirm task"
                    className="h-6 gap-1 px-1.5"
                  />
                  {task.type === "approve" && (
                    <RcRejectButton taskId={task.id} rcId={task.rcId} />
                  )}
                </TaskRowStatus>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export default RcTasksSection;
